import { useEffect, useState, createContext, useContext } from 'react';
import { setRazdelIcon } from './favicon.js';
import { Routes, Route, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { api } from './api.js';
import Calculator from './components/Calculator.jsx';

import Login, { ForcedPasswordChange } from './pages/Login.jsx';
import Dashboard from './pages/Dashboard.jsx';
import Products from './pages/Products.jsx';
import StockTree from './pages/StockTree.jsx';
import ProductView from './pages/ProductView.jsx';
import Partners from './pages/Partners.jsx';
import PartnerView from './pages/PartnerView.jsx';
import Documents from './pages/Documents.jsx';
import DocumentView from './pages/DocumentView.jsx';
import NewDocument from './pages/NewDocument.jsx';
import ChakatProdazhba from './pages/ChakatProdazhba.jsx';
import Payments from './pages/Payments.jsx';
import Debtors from './pages/Debtors.jsx';
import BankImport from './pages/BankImport.jsx';
import Cash from './pages/Cash.jsx';
import Reports from './pages/Reports.jsx';
import Profit from './pages/Profit.jsx';
import AuditLog from './pages/AuditLog.jsx';
import Settings from './pages/Settings.jsx';
import FiscalDevices from './pages/FiscalDevices.jsx';
import Users from './pages/Users.jsx';
import MailSettings from './pages/MailSettings.jsx';
import DebtLetter from './pages/DebtLetter.jsx';
import Transfer from './pages/Transfer.jsx';
import Revision from './pages/Revision.jsx';
import Returns from './pages/Returns.jsx';
import ServiceConsumables from './pages/ServiceConsumables.jsx';
import PrintDocument from './pages/PrintDocument.jsx';
import PrintInvoice from './pages/PrintInvoice.jsx';
import PrintRevisionSheet from './pages/PrintRevisionSheet.jsx';
import Invoicing from './pages/Invoicing.jsx';
import InvoiceImportView from './pages/InvoiceImportView.jsx';
import PurchaseOrders, { PurchaseOrderView, PrintPurchaseOrder } from './pages/PurchaseOrders.jsx';
import Offers, { OfferView, PrintOffer } from './pages/Offers.jsx';
import Deliveries from './pages/Deliveries.jsx';
import Services from './pages/Services.jsx';
import ThemeToggle, { applyThemeClass, savedTheme } from './components/ThemeToggle.jsx';
import PriceLists from './pages/PriceLists.jsx';
import Payroll from './pages/Payroll.jsx';
import PrintPayslip from './pages/PrintPayslip.jsx';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [izlizane, setIzlizane] = useState(null);
  const { pathname } = useLocation();

  /*
   * Печатните изгледи са винаги светли — фактурата и протоколът отиват
   * на хартия, тъмна или тюркоазена тема там няма място. Извън тях се
   * връща избраната тема.
   */
  useEffect(() => {
    applyThemeClass(pathname.startsWith('/print/') ? 'light' : savedTheme());
    setRazdelIcon(pathname);
  }, [pathname]);

  useEffect(() => {
    const pri = (e) => { setIzlizane(e.detail); setUser(null); };
    window.addEventListener('tefter:signed-out', pri);
    return () => window.removeEventListener('tefter:signed-out', pri);
  }, []);

  useEffect(() => {
    api.get('/api/auth/me')
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="empty">Зареждане…</div>;

  if (!user) {
    return (
      <AuthContext.Provider value={{ user, setUser }}>
        <Login onLogin={(u) => { setIzlizane(null); setUser(u); }} notice={izlizane} />
      </AuthContext.Provider>
    );
  }

  // Временна парола от администратора — първо своя, после работа.
  if (user.mustChangePassword) {
    return (
      <AuthContext.Provider value={{ user, setUser }}>
        <ForcedPasswordChange user={user} onDone={() => setUser({ ...user, mustChangePassword: false })} />
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      <Routes>
        {/* Печатните изгледи са без навигация — отварят се в нов раздел. */}
        <Route path="/print/document/:id" element={<PrintDocument />} />
        <Route path="/print/invoice/:invoiceNo" element={<PrintInvoice />} />
        <Route path="/print/revision" element={<PrintRevisionSheet />} />
        <Route path="/print/purchase-order/:id" element={<PrintPurchaseOrder />} />
        <Route path="/print/offer/:id" element={<PrintOffer />} />
        <Route path="/print/payslip/:id" element={<PrintPayslip />} />
        <Route path="*" element={<Shell />} />
      </Routes>
    </AuthContext.Provider>
  );
}

function Shell() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Менюто на телефон е изтеглящо се (drawer) — затваря се само при избор
  // на страница, не при всяко презасичане на компонента.
  const [menuOpen, setMenuOpen] = useState(false);
  // Скрито меню вляво на компютър (28.10.2026) — помни се в този браузър.
  const [menuSkrito, setMenuSkrito] = useState(() => {
    try { return localStorage.getItem('tefter.menuSkrito') === '1'; } catch { return false; }
  });
  const smeniMenu = () => setMenuSkrito((v) => {
    try { localStorage.setItem('tefter.menuSkrito', v ? '0' : '1'); } catch { /* без памет */ }
    return !v;
  });
  // Бройката до „Чакат продажба“ (20.10.2026) — опреснява се на 2 минути.
  const [chakat, setChakat] = useState(null);
  useEffect(() => {
    let alive = true;
    const vzemi = () => api.get('/api/chakat-prodazhba/broi')
      .then((r) => { if (alive) setChakat(r.broi); }).catch(() => {});
    vzemi();
    const t = setInterval(vzemi, 120_000);
    // Страницата „Чакат продажба“ казва бройката веднага, щом я прочете (29.10.2026).
    const otStranicata = (e) => { if (alive && e.detail != null) setChakat(e.detail); };
    window.addEventListener('chakat-broi', otStranicata);
    return () => { alive = false; clearInterval(t); window.removeEventListener('chakat-broi', otStranicata); };
  }, [location.pathname === '/chakat-prodazhba']);
  // Калкулаторът (13.10.2026) — бутон в менюто или F9 отвсякъде.
  const [calcOpen, setCalcOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'F9') { e.preventDefault(); setCalcOpen((v) => !v); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  /*
   * При напускане на всяка страница пускаме дължимите служебни извеждания
   * (разсрочени продажби) — нарочно не веднага след бона, за да не излязат
   * двата бона гръб до гръб на лентата на апарата. Безобидно повикване:
   * ако няма чакащо, сървърът не прави нищо.
   */
  useEffect(() => {
    /*
     * Току-що записаната продажба чака на своята страница — служебният ѝ
     * бон тръгва при „Печат" (DocumentView) или когато операторът я
     * напусне, не веднага след касовия бон (01.10.2026).
     */
    const hold = zadarzhanaProdazhba();
    if (hold && location.pathname === `/documents/${hold}`) {
      api.post('/api/cash/fiscal-queue/flush-service-out', { holdDocumentId: hold }).catch(() => {});
      return;
    }
    if (hold) pustniZadarzhana();
    api.post('/api/cash/fiscal-queue/flush-service-out', {}).catch(() => {});
  }, [location.pathname]);

  async function logout() {
    await api.post('/api/auth/logout').catch(() => {});
    setUser(null);
    navigate('/');
  }

  const canWrite = user.role !== 'auditor';

  return (
    <div className="app">
      <div className="mobile-topbar">
        <button
          type="button"
          className="menu-btn"
          aria-label="Меню"
          onClick={() => setMenuOpen((v) => !v)}
        >
          ☰
        </button>
        <NavLink to="/" end onClick={() => setMenuOpen(false)}>
          <strong>Тефтер</strong>
          <span>Доктор Принтер ЕООД</span>
        </NavLink>
      </div>

      <div
        className={`sidebar-backdrop ${menuOpen ? 'open' : ''}`}
        onClick={() => setMenuOpen(false)}
      />

      <Calculator open={calcOpen} onClose={() => setCalcOpen(false)} />

      <aside className={`sidebar ${menuOpen ? 'open' : ''} ${menuSkrito ? 'skrito' : ''}`}>
        <div className="brand-red">
          <NavLink to="/" end className="brand">
            <strong>Тефтер</strong>
            <span>Доктор Принтер ЕООД</span>
          </NavLink>
          <button type="button" className="menu-skrij" onClick={smeniMenu}
            title={menuSkrito ? 'Покажи менюто' : 'Скрий менюто'}
            aria-label={menuSkrito ? 'Покажи менюто' : 'Скрий менюто'}>
            {menuSkrito ? '›' : '‹'}
          </button>
        </div>

        <nav className="nav">
          <div className="group-label">Номенклатури</div>
          <NavLink to="/stock">Наличности</NavLink>
          <NavLink to="/partners">Контрагенти</NavLink>
          <NavLink to="/price-lists">Ценови листи</NavLink>
          <button
            type="button"
            className={`nav-btn ${calcOpen ? 'active' : ''}`}
            title="Калкулатор — F9"
            onClick={() => { setCalcOpen((v) => !v); setMenuOpen(false); }}
          >
            Калкулатор <span className="muted" style={{ fontSize: 11 }}>F9</span>
          </button>

          <div className="group-label">Работа</div>
          {canWrite && (
            <NavLink
              to="/documents/new/sale"
              className={({ isActive }) =>
                isActive || location.pathname.startsWith('/documents/new') ? 'active' : ''}
            >
              Продажба / доставка
            </NavLink>
          )}
          {canWrite && (
            <NavLink to="/chakat-prodazhba">
              Чакат продажба
              {chakat > 0 && <span className="badge warn" style={{ marginLeft: 6, fontSize: 11 }}>{chakat}</span>}
            </NavLink>
          )}
          {canWrite && <NavLink to="/offers">Оферти</NavLink>}
          {canWrite && <NavLink to="/transfer">Прехвърляне</NavLink>}
          {canWrite && <NavLink to="/revision">Ревизия</NavLink>}
          {canWrite && <NavLink to="/returns">Връщане на стоки</NavLink>}
          {canWrite && <NavLink to="/invoicing">Фактуриране</NavLink>}
          <NavLink to="/purchase-orders">Поръчки към доставчик</NavLink>
          <NavLink to="/deliveries">Доставки до клиенти</NavLink>
          <NavLink to="/services">Услуги</NavLink>
          {/*
           * За одитор „Документи" няма къде другаде да излезе — той няма
           * достъп до „Продажба / доставка", в която вече е вграден списъкът.
           * За администратор/оператор връзката води към всички видове без
           * филтър — продажби/доставки вече са вградени там, но ревизии,
           * прехвърляния и връщания нямат друг общ преглед.
           */}
          {!canWrite && <NavLink to="/documents?type=sale" end>Документи</NavLink>}
          {canWrite && <NavLink to="/documents" end>Документи</NavLink>}
          <NavLink to="/cash">Каса</NavLink>
          <NavLink to="/payments">Плащания</NavLink>
          <NavLink to="/debtors">Длъжници</NavLink>

          <div className="group-label">Справки</div>
          <NavLink to="/reports">Справки</NavLink>
          <NavLink to="/profit">Печалба</NavLink>

          <div className="group-label">Настройки</div>
          {/* Т. 19: одиторският профил трябва да стига до конфигурационните
              параметри — затова връзките се виждат и от него, за преглед. */}
          {(user.role === 'admin' || user.role === 'auditor') && (
            <>
              <NavLink to="/mail" end>Поща</NavLink>
              {/* Подредбата на писмата стои под самата поща, изтеглена
                  навътре — оттам се сменя текстът, който тръгва навън. */}
              <NavLink to="/mail/debt-letter" className="sub">Мейл към длъжници</NavLink>
            </>
          )}
          <NavLink to="/users">
            {user.role === 'admin' ? 'Служители и достъп' : 'Моят достъп'}
          </NavLink>
          <NavLink to="/audit">Одитен дневник</NavLink>
          {/* Т. 19: одиторският профил трябва да стига до конфигурационните
              параметри — затова връзката се вижда и от него. */}
          {(user.role === 'admin' || user.role === 'auditor') && (
            <NavLink to="/settings">Настройки на системата</NavLink>
          )}
          {canWrite && (
            <NavLink to="/service-consumables">Тонер/чип на услугите</NavLink>
          )}
          {(user.role === 'admin' || user.role === 'auditor') && (
            <NavLink to="/fiscal">Фискални устройства</NavLink>
          )}
          {/* Само Калин — проверено и на сървъра по user id, не по роля
              (решение на Калин, 01.10.2026): друг бъдещ админ не бива да
              вижда заплатите просто защото е админ. */}
          {Number(user.id) === 2 && <NavLink to="/payroll">Заплати</NavLink>}
        </nav>

        <div className="who">
          <b>{user.fullName}</b>
          {roleLabel(user.role)} · оператор {user.operatorCode}
          <div style={{ marginTop: 8, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <button className="link" onClick={logout}>Изход</button>
            <ThemeToggle />
          </div>
        </div>
      </aside>

      <main className="main">
        <TestModeBanner />
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/stock" element={<StockTree />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/new" element={<ProductView mode="new" />} />
          <Route path="/products/:id" element={<ProductView />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/partners/new" element={<PartnerView mode="new" />} />
          <Route path="/partners/:id" element={<PartnerView />} />
          <Route path="/transfer" element={<Transfer />} />
          <Route path="/revision" element={<Revision />} />
          <Route path="/returns" element={<Returns />} />
          <Route path="/price-lists" element={<PriceLists />} />
          <Route path="/invoicing" element={<Invoicing />} />
          <Route path="/purchase-orders" element={<PurchaseOrders />} />
          <Route path="/deliveries" element={<Deliveries />} />
          <Route path="/services" element={<Services />} />
          <Route path="/purchase-orders/:id" element={<PurchaseOrderView />} />
          <Route path="/offers" element={<Offers />} />
          <Route path="/offers/:id" element={<OfferView />} />
          <Route path="/documents" element={<Documents />} />
          <Route path="/documents/new/:docType" element={<NewDocument />} />
          <Route path="/chakat-prodazhba" element={<ChakatProdazhba />} />
          <Route path="/documents/:id/continue" element={<NewDocument />} />
          <Route path="/documents/:editId/edit" element={<NewDocument />} />
          <Route path="/documents/:id" element={<DocumentView />} />
          <Route path="/invoice-imports/:id" element={<InvoiceImportView />} />
          <Route path="/payments" element={<Payments />} />
          <Route path="/debtors" element={<Debtors />} />
          <Route path="/bank-import" element={<BankImport />} />
          <Route path="/cash" element={<Cash />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/profit" element={<Profit />} />
          <Route path="/audit" element={<AuditLog />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/service-consumables" element={<ServiceConsumables />} />
          <Route path="/fiscal" element={<FiscalDevices />} />
          <Route path="/users" element={<Users />} />
          <Route path="/mail" element={<MailSettings />} />
          <Route path="/mail/debt-letter" element={<DebtLetter />} />
          {Number(user.id) === 2 && <Route path="/payroll" element={<Payroll />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

/**
 * Лента, докато системата се пробва.
 *
 * Стои на всяка страница нарочно: докато режимът е включен, въведеното
 * тук не е истинско, а старата програма още води продажбите. Най-скъпата
 * грешка би била някой да помисли обратното.
 */
function TestModeBanner() {
  const [state, setState] = useState(null);

  useEffect(() => {
    api.get('/api/test-mode').then(setState).catch(() => setState(null));
  }, []);

  if (!state?.enabled) return null;

  return (
    <div className="test-banner">
      <strong>ПРОБЕН РЕЖИМ</strong>
      <span>
        Въведеното тук е за проба: не взима УНП, не яде от номерата на
        фактурите, не стига до касовия апарат и не влиза в справките.
        Продажбите се водят в старата програма.
      </span>
    </div>
  );
}

function roleLabel(role) {
  return { admin: 'Администратор', manager: 'Мениджър', operator: 'Оператор', auditor: 'Одитор' }[role] ?? role;
}

/*
 * Задържаната продажба — записана току-що, служебният ѝ бон чака, докато
 * операторът е на страницата ѝ. Пази се в sessionStorage: по раздел на
 * браузъра, губи се със затварянето му (тогава бонът тръгва при
 * следващото отваряне или след 10 минути от сървъра).
 */
export function zadarzhiProdazhba(id) {
  try { sessionStorage.setItem('tefter_hold_service', String(id)); } catch { /* няма */ }
}
export function zadarzhanaProdazhba() {
  try { return Number(sessionStorage.getItem('tefter_hold_service')) || null; } catch { return null; }
}
export function pustniZadarzhana() {
  try { sessionStorage.removeItem('tefter_hold_service'); } catch { /* няма */ }
}
